import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Copy, Trash2, Plus, Calendar, Link2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useInvitations,
  useCreateInvitation,
  useDeleteInvitation,
  copyInviteUrl,
} from '@/hooks/useInvitations';
import { cn } from '@/lib/utils';
import type { AlbumInvitationWithUrl } from '@omokage/shared';

interface InvitationDialogProps {
  albumId: string;
  albumName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface InvitationItemProps {
  invitation: AlbumInvitationWithUrl;
  onDelete: (invitationId: string) => void;
}

/**
 * Validate expiry days for invitation
 * Returns valid number or null if invalid
 */
function validateInvitationDays(value: string): number | null {
  if (value === '') return null; // Empty means no expiration

  const num = Number(value);
  // Check for integer, within range (1-30 days for invitations), and not NaN/Infinity
  if (!Number.isInteger(num) || num < 1 || num > 30 || !Number.isFinite(num)) {
    return null;
  }
  return num;
}

function InvitationItem({ invitation, onDelete }: InvitationItemProps) {
  const isExpired = useMemo(
    // eslint-disable-next-line react-hooks/purity
    () => invitation.expiresAt !== null && invitation.expiresAt * 1000 < Date.now(),
    [invitation.expiresAt]
  );
  const createdDate = new Date(invitation.createdAt * 1000).toLocaleDateString();
  const expiryDate = invitation.expiresAt
    ? new Date(invitation.expiresAt * 1000).toLocaleDateString()
    : '無期限';

  const handleCopy = async () => {
    const copied = await copyInviteUrl(invitation.inviteUrl);
    if (copied) {
      toast.success('招待リンクをコピーしました');
    } else {
      toast.error('リンクのコピーに失敗しました');
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 rounded-lg border',
        'bg-white/50 dark:bg-card/50 border-border',
        'hover:bg-white/80 dark:hover:bg-card/80 transition-colors'
      )}
    >
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-mono text-foreground truncate">{invitation.inviteUrl}</p>
          {isExpired && (
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full bg-destructive/10 text-destructive"
              role="status"
            >
              期限切れ
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>作成日: {createdDate}</span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            有効期限: {expiryDate}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1 ml-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          aria-label="招待リンクをコピー"
          className="text-accent hover:bg-accent/10"
        >
          <Copy className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(invitation.id)}
          aria-label="招待リンクを削除"
          className="text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function InvitationSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}

export function InvitationDialog({
  albumId,
  albumName,
  open,
  onOpenChange,
}: InvitationDialogProps) {
  const [expiresInDays, setExpiresInDays] = useState<string>('7');

  const { data: invitations, isLoading } = useInvitations(albumId);
  const createInvitation = useCreateInvitation(albumId);
  const deleteInvitation = useDeleteInvitation();

  const handleCreateInvitation = async () => {
    try {
      // Validate input - validateInvitationDays returns null for both invalid and empty string
      const validatedDays = validateInvitationDays(expiresInDays);

      // Check if it's an invalid number (not empty, but failed validation)
      if (expiresInDays !== '' && validatedDays === null) {
        toast.error('有効期限は1〜30の整数で入力してください');
        return;
      }

      const invitation = await createInvitation.mutateAsync({
        expiresInDays: validatedDays,
      });

      // Auto-copy the new invitation link
      const copied = await copyInviteUrl(invitation.inviteUrl);
      if (copied) {
        toast.success('招待リンクを作成してコピーしました');
      } else {
        toast.success('招待リンクを作成しました');
      }

      setExpiresInDays('7');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '招待リンクの作成に失敗しました';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (invitationId: string) => {
    try {
      await deleteInvitation.mutateAsync({ invitationId, albumId });
      toast.success('招待リンクを削除しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '招待リンクの削除に失敗しました';
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="custom-scrollbar sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>コラボレーターを招待: {albumName}</DialogTitle>
          <DialogDescription>
            このアルバムに他のユーザーを招待するためのリンクを作成します。招待されたユーザーは写真の追加・削除・閲覧ができます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Create new invitation */}
          <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/50">
            <h3 className="text-sm font-medium">新しい招待リンクを作成</h3>
            <div className="flex gap-2">
              <div className="flex-1">
                <label htmlFor="expiry" className="text-xs text-muted-foreground mb-1 block">
                  有効期限（1〜30日）
                </label>
                <Input
                  id="expiry"
                  type="number"
                  min="1"
                  max="30"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  placeholder="30"
                  className="h-9"
                />
              </div>
              <Button
                onClick={handleCreateInvitation}
                disabled={createInvitation.isPending}
                className="mt-auto"
              >
                <Plus className="mr-2 h-4 w-4" />
                {createInvitation.isPending ? '作成中...' : 'リンクを作成'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              コラボレーションしたい相手にこのリンクを共有してください。招待を受け入れるとアルバムに参加できます。
            </p>
          </div>

          {/* Existing invitations */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">有効な招待リンク</h3>
            {isLoading ? (
              <InvitationSkeleton />
            ) : !invitations || invitations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>招待リンクはありません</p>
                <p className="text-sm mt-2">上のフォームから招待リンクを作成してください</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invitations.map((invitation) => (
                  <InvitationItem
                    key={invitation.id}
                    invitation={invitation}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
